// Product Brands dashboard: header (title + stats + "+ Brand") over a single
// flat brands table with a client-side name search. A single-list
// simplification of the Product Attributes dashboard — brands have no
// types/values hierarchy, so there is no rail and no per-type view.

import { Icon } from 'design-system';
import {
  Box,
  Flex,
  HStack,
  IconButtonWithTooltip,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
} from 'design-system';
import { useGetProductBrandsApi, useGetProductBrandUsageApi, usageForBrand } from 'apis/product-brands';
import { useEffect, useMemo, useState } from 'react';
import { FiPlus, FiSearch, FiX } from 'react-icons/fi';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

import { BrandFormModal } from '../components/product-brands/brand-form-modal';
import { BrandsTable } from '../components/product-brands/brands-table';

export const ProductBrandsScreen = () => {
  const dispatch = useAppDispatch();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading } = useGetProductBrandsApi();
  const { data: usageData } = useGetProductBrandUsageApi();
  const brands = useMemo(() => data?.data ?? [], [data]);
  const usage = usageData?.data;

  useEffect(() => {
    dispatch(changeTitle('Product Brands'));
  }, [dispatch]);

  const isSearching = searchQuery.trim().length > 0;
  const visibleBrands = useMemo(() => {
    if (!isSearching) return brands;
    const query = searchQuery.trim().toLowerCase();
    return brands.filter((brand) => brand.name.toLowerCase().includes(query));
  }, [brands, searchQuery, isSearching]);

  const stats = useMemo(() => {
    const active = brands.filter((brand) => brand.isActive).length;
    const inUse = brands.filter((brand) => usageForBrand(usage, brand.name) > 0).length;
    return [
      { label: 'Brands', value: brands.length },
      { label: 'Active', value: active },
      { label: 'In use', value: inUse },
    ];
  }, [brands, usage]);

  return (
    <Box p='4'>
      <Flex align='center' justify='space-between' mb='4' wrap='wrap' gap='3'>
        <Box>
          <Text fontSize='xl' fontWeight='bold'>
            Product Brands
          </Text>
          <HStack mt='1' spacing='4'>
            {stats.map((stat) => (
              <Text key={stat.label} fontSize='sm' color='gray.500'>
                {stat.label}{' '}
                <Text as='span' fontWeight='semibold' color='gray.700'>
                  {stat.value}
                </Text>
              </Text>
            ))}
          </HStack>
        </Box>
        <IconButtonWithTooltip
          aria-label='Add Brand'
          label='Add Brand'
          Icon={<Icon as={FiPlus} />}
          onClick={() => setIsCreateOpen(true)}
        />
      </Flex>

      <Flex align='center' gap='3' mb='4' wrap='wrap'>
        <InputGroup maxW='320px' size='sm'>
          <InputLeftElement pointerEvents='none'>
            <Icon as={FiSearch} color='gray.400' />
          </InputLeftElement>
          <Input
            placeholder='Search brands…'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            borderRadius='md'
          />
          {isSearching && (
            <InputRightElement>
              <IconButtonWithTooltip
                size='xs'
                label='Clear search'
                aria-label='clear-search-btn'
                Icon={<Icon as={FiX} />}
                variant='unstyled'
                onClick={() => setSearchQuery('')}
              />
            </InputRightElement>
          )}
        </InputGroup>
      </Flex>

      <BrandsTable brands={visibleBrands} usage={usage} isLoading={isLoading} />

      <BrandFormModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
    </Box>
  );
};
